import { useEffect, useRef, useState } from 'react';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import { ChatWindow, pinnedFirst } from '../components/ChatWidget';
import { CustomerChatWindow, canSeeAllKh, canSeeSaleKh, canMuteKh, StartCustChat, needsChatChoice } from '../components/CustomerChat';
import { chatUsers, mediaUrl, listChatMedia, listChatLinks, searchChatMessages, searchAllChatMessages, muteConversation, openDirect, createGroup, pinConversation, markRead, markConversationUnread, hideConversation, deleteGroupChat, removeGroupMember, renameGroup, createChatTask, acceptChatTask, completeChatTask, deleteChatTask } from '../api/chat';
import { onAttachmentClick } from '../download';
import { pushBackHandler } from '../backButton';
import { custListMedia, custListLinks, custSearchMessages, custSearchAllMessages, custMute, custMuteAll, custMuteChannel, custPinConversation, createCustConversation, getMyChannels, custMarkRead, custMarkUnread, custHide, getStarConfig, setStarConfig, getBotConfig, setBotConfig, createCustTask, acceptCustTask, completeCustTask, deleteCustTask } from '../api/customerChat';
import ConvMenu from '../components/ConvMenu';
import ChatTasksPanel, { openTaskCount } from '../components/ChatTasksPanel';
import PartnerAccount from '../components/PartnerAccount';
import { searchCustomers } from '../api/customers';
import { SVC_ORDER, SVC_SHORT, SVC_LABEL, groupByCustomer } from '../utils/custChannels';
import { fmtSize } from '../utils/chatFiles';

// ===== Trang "Tin nhắn" (kiểu Messenger) =====
// 3 cột: [danh sách 2 tab Nội bộ | Khách hàng] [khung chat] [bảng thông tin].
// Cột giữa NHÚNG LẠI ChatWindow / CustomerChatWindow của widget (CSS .chatpage-thread ép đầy cột)
// -> mọi tính năng gửi/nhận/reply/cảm xúc dùng chung một nguồn, sửa 1 nơi chạy cả 2.

const initials = (name) => (name || '?').trim().split(/\s+/).slice(-1)[0]?.[0]?.toUpperCase() || '?';
const fmtTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (dt.toDateString() === new Date().toDateString()) return dt.toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' });
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};
// Bỏ dấu TỪNG KÝ TỰ (giữ nguyên độ dài chuỗi) để tìm vị trí khớp không phân biệt dấu.
const foldStr = (s) => [...String(s)].map((c) => c.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D')).join('');
// Cắt đoạn văn quanh chỗ khớp + IN ĐẬM phần khớp (kiểu kết quả tìm kiếm FB).
function excerpt(text, q) {
  const t = String(text || '');
  const i = foldStr(t).toLowerCase().indexOf(foldStr(q).toLowerCase());
  if (i === -1) return t.slice(0, 90);
  const start = Math.max(0, i - 28);
  return [
    (start > 0 ? '…' : '') + t.slice(start, i),
    <b key="hit">{t.slice(i, i + q.length)}</b>,
    t.slice(i + q.length, i + q.length + 60),
  ];
}

const Ava = ({ conv, size = 42 }) => {
  const st = { width: size, height: size, fontSize: size * 0.38 };
  if (conv.kind === 'customer') return <div className="chat-avatar cust" style={st}>{initials(conv.name)}</div>;
  if (conv.type === 'group') return <div className="chat-avatar group" style={st}>👥</div>;
  return (
    <div className="chat-avatar" style={st}>
      {conv.other?.avatar ? <img className="chat-avatar-img" src={conv.other.avatar} alt="" /> : initials(conv.name)}
    </div>
  );
};

// Popup ⚙️ CÀI ĐẶT tin nhắn khách (nút cạnh bút tạo tin mới): gộp bật/tắt thông báo (mọi khách + từng
// luồng) cho GĐ/TP, và NGƯỠNG đánh dấu sao KH (chỉ GĐ) — cho gọn danh sách.
function ChatSettingsModal({ onClose, isGD, custMutedAll, toggleCustMuteAll, custMutedSvc, toggleCustChannel }) {
  const [mh, setMh] = useState('');
  const [vc, setVc] = useState('');
  const [starLoading, setStarLoading] = useState(isGD);
  const [saving, setSaving] = useState(false);
  const [starMsg, setStarMsg] = useState('');
  // Trợ lý đơn hàng tự động (kill-switch + kênh áp dụng). Lưu ngay khi bấm.
  const [botOn, setBotOn] = useState(false);
  const [botChans, setBotChans] = useState([]); // rỗng = mọi kênh
  const [botLoading, setBotLoading] = useState(isGD);
  const [botMsg, setBotMsg] = useState('');
  const fmt = (n) => (Number(n) || 0).toLocaleString('vi-VN');
  const parse = (s) => Number(String(s).replace(/[^\d]/g, '')) || 0;
  useEffect(() => {
    if (!isGD) return;
    getStarConfig()
      .then((c) => { setMh(fmt(c.mhThreshold)); setVc(fmt(c.vcThreshold)); })
      .catch(() => setStarMsg('Không tải được cấu hình'))
      .finally(() => setStarLoading(false));
    getBotConfig()
      .then((c) => { setBotOn(!!c.orderStatusEnabled); setBotChans(c.orderStatusChannels || []); })
      .catch(() => {})
      .finally(() => setBotLoading(false));
  }, [isGD]);
  const persistBot = async (nextOn, nextChans) => {
    setBotOn(nextOn); setBotChans(nextChans); setBotMsg('');
    try {
      const c = await setBotConfig({ orderStatusEnabled: nextOn, orderStatusChannels: nextChans });
      setBotOn(!!c.orderStatusEnabled); setBotChans(c.orderStatusChannels || []); setBotMsg('Đã lưu ✓');
    } catch { setBotMsg('Lưu thất bại'); }
  };
  const saveStar = async () => {
    setSaving(true); setStarMsg('');
    try { await setStarConfig(parse(mh), parse(vc)); setStarMsg('Đã lưu ✓'); }
    catch (e) { setStarMsg(e?.response?.data?.message || 'Lưu thất bại'); }
    finally { setSaving(false); }
  };
  return (
    <div className="cust-star-backdrop" onClick={onClose}>
      <div className="cust-star-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cust-star-head">
          <span>⚙️ Cài đặt tin nhắn khách</span>
          <button type="button" className="cust-star-x" onClick={onClose} aria-label="Đóng">✕</button>
        </div>
        <div className="cust-star-body">
          <div className="cust-set-section">
            <div className="cust-set-title">🔔 Thông báo</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
              <span className="small">Thông báo tất cả khách</span>
              <button type="button" onClick={toggleCustMuteAll} className={'cust-notif-toggle' + (custMutedAll ? ' off' : '')}>
                {custMutedAll ? '🔕 Đang tắt' : '🔔 Đang bật'}
              </button>
            </div>
            <div className="small muted" style={{ marginBottom: 6 }}>Thông báo theo luồng</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {SVC_ORDER.map((t) => {
                const on = !custMutedSvc.includes(t);
                return (
                  <button key={t} type="button" className={'cust-notif-chip' + (on ? ' on' : '')} onClick={() => toggleCustChannel(t)}
                    title={(on ? 'Đang BẬT' : 'Đang TẮT') + ` luồng ${SVC_LABEL[t]}`}>
                    {on ? '🔔' : '🔕'} {SVC_LABEL[t]}
                  </button>
                );
              })}
            </div>
          </div>

          {isGD && (
            <div className="cust-set-section">
              <div className="cust-set-title">⭐ Ngưỡng đánh dấu sao khách hàng</div>
              <div className="small muted" style={{ marginBottom: 10 }}>
                KH có doanh thu trung bình/tháng (bảng lũy kế năm nay) vượt <b>một trong hai</b> ngưỡng sẽ được gắn ⭐.
              </div>
              {starLoading ? <div className="small muted">Đang tải…</div> : (
                <>
                  <label className="cust-star-label">Doanh thu mua hộ lớn hơn (đ)</label>
                  <input className="cust-star-input" value={mh} inputMode="numeric" onChange={(e) => setMh(fmt(parse(e.target.value)))} />
                  <label className="cust-star-label">Doanh thu vận chuyển lớn hơn (đ)</label>
                  <input className="cust-star-input" value={vc} inputMode="numeric" onChange={(e) => setVc(fmt(parse(e.target.value)))} />
                  {starMsg && <div className="small" style={{ marginTop: 8, color: starMsg.includes('✓') ? 'var(--ok)' : 'var(--danger)' }}>{starMsg}</div>}
                  <button type="button" className="cust-star-save" onClick={saveStar} disabled={saving}>{saving ? 'Đang lưu…' : 'Lưu ngưỡng sao'}</button>
                </>
              )}
            </div>
          )}

          {isGD && (
            <div className="cust-set-section">
              <div className="cust-set-title">🤖 Trợ lý đơn hàng tự động</div>
              <div className="small muted" style={{ marginBottom: 10 }}>
                Khi khách gửi <b>mã đơn</b> vào chat, hệ thống tự tra SocSon và trả trạng thái ngay. Chỉ trả đơn của <b>đúng khách</b> đang chat.
              </div>
              {botLoading ? <div className="small muted">Đang tải…</div> : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                    <span className="small">Tự trả trạng thái đơn</span>
                    <button type="button" onClick={() => persistBot(!botOn, botChans)} className={'cust-notif-toggle' + (botOn ? '' : ' off')}>
                      {botOn ? '🤖 Đang bật' : '⭘ Đang tắt'}
                    </button>
                  </div>
                  <div className="small muted" style={{ marginBottom: 6 }}>Áp dụng cho luồng (không chọn = tất cả)</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {SVC_ORDER.map((t) => {
                      const on = botChans.includes(t);
                      return (
                        <button key={t} type="button" className={'cust-notif-chip' + (on ? ' on' : '')} disabled={!botOn}
                          onClick={() => persistBot(botOn, on ? botChans.filter((x) => x !== t) : [...botChans, t])}
                          title={`${on ? 'Đang áp dụng' : 'Bấm để áp dụng'} luồng ${SVC_LABEL[t]}`}>
                          {on ? '✓ ' : ''}{SVC_LABEL[t]}
                        </button>
                      );
                    })}
                  </div>
                  {botMsg && <div className="small" style={{ marginTop: 8, color: botMsg.includes('✓') ? 'var(--ok)' : 'var(--danger)' }}>{botMsg}</div>}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ChatPage() {
  const chat = useChat();
  const { me, convs, custConvs, internalUnread, custUnread, selectPageConv, setChatPageActive, onlineIds, patchConv, patchCustConv, removeConvsLocal, deepConv, clearDeepConv } = chat;
  const { setMe } = useAuth();
  // (TP/GĐ) TẮT ÂM TOÀN BỘ tin khách — nút chỉ hiện ở tab Khách hàng.
  const canMute = canMuteKh(me);
  const isGD = me?.role === 'GD' || me?.role === 'CV'; // chỉ GĐ/CV set ngưỡng đánh dấu sao
  const [showSettings, setShowSettings] = useState(false); // popup Cài đặt tin nhắn (thông báo + ngưỡng sao)
  const [custMutedAll, setCustMutedAll] = useState(!!me?.custPushMutedAll);
  useEffect(() => { setCustMutedAll(!!me?.custPushMutedAll); }, [me?.custPushMutedAll]);
  const toggleCustMuteAll = async () => {
    const next = !custMutedAll;
    setCustMutedAll(next); // cập nhật ngay cho mượt
    try {
      const r = await custMuteAll(next);
      setCustMutedAll(!!r.muted);
      setMe({ ...me, custPushMutedAll: !!r.muted }); // giữ trạng thái sau remount
    } catch { setCustMutedAll(!next); } // lỗi -> hoàn lại
  };
  // (TP/GĐ) TẮT/BẬT thông báo hệ thống KHÁCH theo TỪNG LUỒNG (kd/cskh/dvkh). mutedSvc = luồng đang TẮT.
  const [custMutedSvc, setCustMutedSvc] = useState(me?.custMutedSvc || []);
  useEffect(() => { setCustMutedSvc(me?.custMutedSvc || []); }, [me?.custMutedSvc]);
  const toggleCustChannel = async (svcType) => {
    const willMute = !custMutedSvc.includes(svcType); // đang BẬT -> bấm để TẮT luồng này
    const next = willMute ? [...custMutedSvc, svcType] : custMutedSvc.filter((s) => s !== svcType);
    setCustMutedSvc(next); // cập nhật ngay cho mượt
    try {
      const r = await custMuteChannel(svcType, willMute);
      const svc = r.mutedSvc || next;
      setCustMutedSvc(svc);
      setMe({ ...me, custMutedSvc: svc }); // giữ trạng thái sau remount
    } catch { setCustMutedSvc(custMutedSvc); } // lỗi -> hoàn lại
  };
  const [tab, setTab] = useState('internal'); // tab của TRANG (độc lập với tab widget)
  const [sel, setSel] = useState(null); // hội thoại đang mở ở cột giữa
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [filterSaleUser, setFilterSaleUser] = useState(''); // '' = mọi NV; id sale -> chỉ KH của sale đó
  // Bảng thông tin bên phải: MẶC ĐỊNH ĐÓNG — chỉ mở khi bấm TÊN KH/nhân viên trên đầu khung chat.
  const [showInfo, setShowInfo] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const h = () => setVw(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const mobile = vw <= 980;

  // Mở 1 hội thoại trên MOBILE = màn chat toàn khung (thiết kế): ẩn bottom-nav để thanh nhập có
  // trọn chỗ dưới cùng. Rời hội thoại / rời trang -> hiện lại bottom-nav.
  useEffect(() => {
    const on = mobile && !!sel;
    document.body.classList.toggle('ft-chat-thread', on);
    if (!on) return () => document.body.classList.remove('ft-chat-thread');
    const popBack = pushBackHandler(() => setSel(null)); // back cứng -> rời hội thoại về danh sách
    return () => {
      document.body.classList.remove('ft-chat-thread');
      popBack();
    };
  }, [mobile, sel]);

  // Báo cho ChatContext biết đang xem hội thoại nào (tự đã-đọc, không kêu); rời trang thì huỷ.
  useEffect(() => {
    selectPageConv(sel);
    return () => selectPageConv(null);
  }, [sel, selectPageConv]);
  // Bấm THÔNG BÁO (đẩy) -> mở ĐÚNG hội thoại chi tiết ở trang này, không chỉ dừng ở màn danh sách.
  // ChatContext.deepConv được set khi tap thông báo; App tự chuyển sang trang Tin nhắn (nghe 'ft:open-conv-page').
  useEffect(() => {
    if (!deepConv || !deepConv.conv) return;
    setTab(deepConv.kind === 'customer' ? 'customer' : 'internal');
    setSel(deepConv.conv);
    clearDeepConv();
  }, [deepConv, clearDeepConv]);
  // Đang đứng trong trang Tin nhắn (kể cả chưa chọn hội thoại) -> chặn tự bung cửa sổ nổi đè lên.
  useEffect(() => {
    setChatPageActive(true);
    return () => setChatPageActive(false);
  }, [setChatPageActive]);

  // Bản "sống" của hội thoại đang mở (unread/muted/members đổi realtime theo danh sách).
  const selLive = sel && ([...convs, ...custConvs].find((c) => String(c.id) === String(sel.id)) || sel);

  const canSeeSale = canSeeSaleKh(me); // GĐ/CV/CSKH/DVKH + TP + PP: thấy tên sale + lọc theo NV
  // Danh sách NV để LỌC = các sale khác nhau trong những hội thoại KH đang thấy (theo phạm vi của mình).
  const saleOptions = [...custConvs.reduce((m, c) => (c.custSaleUser && c.custSaleName ? m.set(String(c.custSaleUser), c.custSaleName) : m), new Map()).entries()]
    .map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'vi'));

  const list = tab === 'customer' ? custConvs : convs;
  const filtered = pinnedFirst(list.filter((c) => {
    const s = q.trim().toLowerCase();
    if (s && !`${c.name} ${c.custCode || ''}`.toLowerCase().includes(s)) return false;
    // Lọc theo NHÂN VIÊN phụ trách (chỉ tab Khách hàng).
    if (tab === 'customer' && canSeeSale && filterSaleUser && String(c.custSaleUser || '') !== filterSaleUser) return false;
    if (filter === 'unread') return (c.unread || 0) > 0;
    if (filter === 'group') return c.type === 'group';
    if (filter === 'unassigned') return !c.assignedTo;
    return true;
  }));

  const pick = (c) => { setSel(c); setShowNew(false); };
  // Nhóm đang mở ở cột giữa bị XOÁ (trưởng nhóm xoá) -> rời hội thoại, không để lại khung "ma".
  useEffect(() => {
    const onRemoved = (e) => { if (sel && String(e.detail?.id || '') === String(sel.id)) setSel(null); };
    window.addEventListener('ft:conv-removed', onRemoved);
    return () => window.removeEventListener('ft:conv-removed', onRemoved);
  }, [sel]);
  // TAB GIÁM SÁT (chỉ GĐ): nhóm GĐ KHÔNG ở trong. Mặc định TẮT tbao; bấm 🔕→🔔 để nhận tin nhóm cần.
  const [obsGroups, setObsGroups] = useState([]);
  useEffect(() => {
    if (tab !== 'observe' || me?.role !== 'GD') return;
    let alive = true;
    import('../api/chat').then(({ listObserverGroups }) => listObserverGroups())
      .then((r) => { if (alive) setObsGroups(r || []); }).catch(() => { if (alive) setObsGroups([]); });
    return () => { alive = false; };
  }, [tab, me]);
  const toggleWatch = async (g) => {
    try {
      const { watchGroup } = await import('../api/chat');
      const r = await watchGroup(g.id, !g.watching);
      setObsGroups((prev) => prev.map((x) => (String(x.id) === String(g.id) ? { ...x, watching: r.watching } : x)));
    } catch (e) { alert(e?.response?.data?.message || 'Không đổi được'); }
  };

  // GHIM HỘI THOẠI lên đầu danh sách (riêng mình, tối đa 3) — nút 📌 hiện khi rê chuột vào dòng.
  const togglePinConv = async (e, c) => {
    e.stopPropagation();
    try {
      if (c.kind === 'customer') {
        const r = await custPinConversation(c.id, !c.pinned);
        patchCustConv(c.id, { pinned: r.pinned });
      } else {
        const r = await pinConversation(c.id, !c.pinned);
        patchConv(c.id, { pinned: r.pinned });
      }
    } catch (err) { alert(err?.response?.data?.message || 'Ghim thất bại'); }
  };

  // ===== MENU NGỮ CẢNH hội thoại (GIỮ trên mobile / CHUỘT PHẢI trên web) — kiểu Zalo =====
  const [menu, setMenu] = useState(null); // { target, x, y, sheet }
  const lpTimer = useRef(null);
  const lpFired = useRef(false);
  const lpStart = useRef({ x: 0, y: 0 });
  const openMenu = (target, x, y, sheet) => setMenu({ target, x, y, sheet });
  // Chuẩn hoá "target" từ 1 hội thoại nội bộ (c) hoặc 1 NHÓM khách hàng (g — gồm nhiều kênh KD/CSKH/Mua).
  const targetOfConv = (c) => ({ id: c.id, ids: [c.id], kind: 'internal', pinned: c.pinned, muted: c.muted, unread: c.unread || 0, name: c.name });
  const targetOfGroup = (g) => ({ id: g.rep.id, ids: g.list.map((x) => x.id), kind: 'customer', pinned: g.pinned, muted: g.rep.muted, unread: g.unread || 0, name: g.name });
  // Props gắn vào mỗi dòng: chuột phải (web) mở popup tại con trỏ; GIỮ ~420ms (mobile) mở bảng trượt đáy.
  const rowMenu = (target) => ({
    onContextMenu: (e) => { e.preventDefault(); openMenu(target, e.clientX, e.clientY, false); },
    onTouchStart: (e) => {
      lpFired.current = false;
      const p = e.touches?.[0];
      const x = p?.clientX || 0;
      const y = p?.clientY || 0;
      lpStart.current = { x, y };
      clearTimeout(lpTimer.current);
      lpTimer.current = setTimeout(() => { lpFired.current = true; if (navigator.vibrate) navigator.vibrate(12); openMenu(target, x, y, true); }, 420);
    },
    onTouchMove: (e) => {
      const p = e.touches?.[0];
      if (p && (Math.abs(p.clientX - lpStart.current.x) > 10 || Math.abs(p.clientY - lpStart.current.y) > 10)) clearTimeout(lpTimer.current);
    },
    onTouchEnd: () => clearTimeout(lpTimer.current),
  });
  // Bấm dòng: nếu vừa GIỮ để hiện menu thì bỏ qua cú click theo sau (không mở nhầm hội thoại).
  const rowClick = (c) => { if (lpFired.current) { lpFired.current = false; return; } pick(c); };

  const doPin = async (t) => {
    try {
      if (t.kind === 'customer') { const r = await custPinConversation(t.id, !t.pinned); patchCustConv(t.id, { pinned: r.pinned }); }
      else { const r = await pinConversation(t.id, !t.pinned); patchConv(t.id, { pinned: r.pinned }); }
    } catch (err) { alert(err?.response?.data?.message || 'Ghim thất bại'); }
  };
  const doMarkUnread = async (t) => {
    try {
      if (t.unread > 0) { // đang có tin mới -> đổi thành ĐÃ ĐỌC
        if (t.kind === 'customer') { await custMarkRead(t.id); patchCustConv(t.id, { unread: 0 }); }
        else { await markRead(t.id); patchConv(t.id, { unread: 0 }); }
      } else { // đánh dấu CHƯA ĐỌC
        if (t.kind === 'customer') { const r = await custMarkUnread(t.id); patchCustConv(t.id, { unread: r.unread || 1 }); }
        else { const r = await markConversationUnread(t.id); patchConv(t.id, { unread: r.unread || 1, lastReadAt: r.lastReadAt }); }
      }
    } catch (err) { alert(err?.response?.data?.message || 'Không đổi được trạng thái đọc'); }
  };
  const doMute = async (t) => {
    const on = !t.muted;
    try {
      if (t.kind === 'customer') { await Promise.all(t.ids.map((id) => custMute(id, on))); t.ids.forEach((id) => patchCustConv(id, { muted: on })); }
      else { const r = await muteConversation(t.id, on); patchConv(t.id, { muted: r.muted }); }
    } catch (err) { alert(err?.response?.data?.message || 'Không đổi được thông báo'); }
  };
  const doHide = async (t) => {
    if (!window.confirm('Xóa hội thoại khỏi danh sách của bạn?\nTin nhắn KHÔNG bị mất — hội thoại sẽ tự hiện lại khi có tin mới.')) return;
    try {
      if (t.kind === 'customer') await Promise.all(t.ids.map((id) => custHide(id)));
      else await hideConversation(t.id);
    } catch (err) { alert(err?.response?.data?.message || 'Xóa hội thoại thất bại'); return; }
    removeConvsLocal(t.ids, t.kind);
    if (sel && t.ids.some((id) => String(id) === String(sel.id))) setSel(null);
  };
  const convMenuEl = menu ? (
    <ConvMenu menu={menu} onClose={() => setMenu(null)} onPin={doPin} onUnread={doMarkUnread} onMute={doMute} onHide={doHide} />
  ) : null;

  // TÌM NỘI DUNG TIN NHẮN (kiểu FB): tên hội thoại vẫn ƯU TIÊN (danh sách lọc phía trên),
  // thêm mục "Tin nhắn" bên dưới. Gõ xong 400ms mới gọi; đổi tab thì tìm theo tab đó.
  const [msgHits, setMsgHits] = useState(null); // null = chưa/đang gõ, [] = không có kết quả
  const qTimer = useRef(null);
  useEffect(() => {
    clearTimeout(qTimer.current);
    const s = q.trim();
    if (s.length < 2) { setMsgHits(null); return; }
    qTimer.current = setTimeout(() => {
      (tab === 'customer' ? custSearchAllMessages(s) : searchAllChatMessages(s))
        .then(setMsgHits)
        .catch(() => setMsgHits([]));
    }, 400);
    return () => clearTimeout(qTimer.current);
  }, [q, tab]);

  // Bấm 1 kết quả tin nhắn: mở đúng hội thoại rồi thử cuộn tới tin đó (tin cũ hơn phần
  // đang tải trong khung thì đành đứng ở cuối — khung chỉ nạp 40 tin gần nhất).
  const openHit = (h) => {
    const conv = list.find((c) => String(c.id) === String(h.convId));
    if (!conv) return;
    pick(conv);
    const prefix = tab === 'customer' ? 'kmsg-' : 'cmsg-';
    let tries = 0;
    const t = setInterval(() => {
      tries += 1;
      const el = document.getElementById(prefix + h.msgId);
      if (el) { // tin đã nằm trong khung -> cuộn tới luôn
        clearInterval(t);
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('chatmsg-flash');
        setTimeout(() => el.classList.remove('chatmsg-flash'), 1400);
      } else if (document.querySelector('[id^="' + prefix + '"]')) {
        // Khung ĐÃ nạp tin ban đầu mà chưa thấy tin cần tìm -> nhờ khung TỰ TẢI TIN CŨ tới khi gặp
        // rồi cuộn tới (scrollToMsg trong ChatWindow). Trước đây chỉ dò DOM nên tin cũ "bấm vào ko ra".
        clearInterval(t);
        window.dispatchEvent(new CustomEvent('ft:jump-msg', { detail: { convId: conv.id, id: h.msgId } }));
      } else if (tries > 25) clearInterval(t); // khung chưa mount kịp -> thôi
    }, 300);
  };
  const switchTab = (t) => { setTab(t); setFilter('all'); setFilterSaleUser(''); };
  const chips = tab === 'customer'
    ? [['all', 'Tất cả'], ['unread', 'Chưa đọc'], ...(canSeeAllKh(me) ? [['unassigned', 'Chưa gán']] : [])]
    : [['all', 'Tất cả'], ['unread', 'Chưa đọc'], ['group', 'Nhóm']];

  const listCol = (
    <div className="chatpage-col">
      <div className="chatpage-listhead">
        <b>Đoạn chat</b>
        <div className="row" style={{ gap: 6, alignItems: 'center' }}>
          {tab === 'customer' && canMute && (
            <button className="chathead-btn" title="Cài đặt tin nhắn khách (bật/tắt thông báo, ngưỡng đánh dấu sao)" onClick={() => setShowSettings(true)}>⚙️</button>
          )}
          <button className="chathead-btn chatpage-newbtn"
            title={tab === 'customer' ? 'Nhắn khách hàng mới (chủ động, không chờ KH nhắn trước)' : 'Cuộc trò chuyện mới'}
            onClick={() => setShowNew((v) => !v)}>✎</button>
          {/* Đối tác (DT) không có sidebar -> nút tài khoản (Đổi thông tin / Đăng xuất) đặt ở đây. */}
          {me?.role === 'DT' && <PartnerAccount />}
        </div>
      </div>
      {/* ĐỐI TÁC (DT): KHÔNG có tab Khách hàng (dữ liệu KH nội bộ). Chỉ chat nội bộ với người được gán. */}
      {me?.role === 'DT' ? null : (
        <div className="chattabs">
          <button type="button" className={'chattab' + (tab === 'internal' ? ' on' : '')} onClick={() => switchTab('internal')}>
            Nội bộ{internalUnread > 0 && <span className="chattab-badge">{internalUnread > 99 ? '99+' : internalUnread}</span>}
          </button>
          <button type="button" className={'chattab' + (tab === 'customer' ? ' on' : '')} onClick={() => switchTab('customer')}>
            Khách hàng{custUnread > 0 && <span className="chattab-badge">{custUnread > 99 ? '99+' : custUnread}</span>}
          </button>
          {me?.role === 'GD' && (
            <button type="button" className={'chattab' + (tab === 'observe' ? ' on' : '')} onClick={() => switchTab('observe')}>
              👁 Giám sát
            </button>
          )}
        </div>
      )}
      <div style={{ padding: '8px 10px 0' }}>
        <div className="chatpage-searchwrap">
          <input className="chat-input" style={{ width: '100%' }} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={tab === 'customer' ? '🔍 Tìm tên, mã KH, nội dung tin…' : tab === 'observe' ? '🔍 Tìm nhóm giám sát…' : '🔍 Tìm tên, nội dung tin nhắn…'} />
          {q && <button type="button" className="chatpage-search-x" title="Xoá tìm kiếm" onClick={() => setQ('')}>×</button>}
        </div>
      </div>
      {tab !== 'observe' && (
        <div className="chatpage-chips">
          {chips.map(([k, label]) => (
            <button key={k} type="button" className={'chatpage-chip' + (filter === k ? ' on' : '')} onClick={() => setFilter(k)}>{label}</button>
          ))}
          {/* Lọc theo NHÂN VIÊN phụ trách (chỉ tab Khách hàng, cho người được thấy tên sale). */}
          {tab === 'customer' && canSeeSale && saleOptions.length > 0 && (
            <select className="cust-filter-sale" value={filterSaleUser} onChange={(e) => setFilterSaleUser(e.target.value)} title="Lọc tin nhắn theo nhân viên phụ trách">
              <option value="">👤 Tất cả nhân viên</option>
              {saleOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
      )}
      {/* Thanh thông báo + ngưỡng sao đã GỘP vào popup ⚙️ Cài đặt (nút cạnh bút) cho gọn danh sách. */}
      {showSettings && (
        <ChatSettingsModal
          isGD={isGD}
          custMutedAll={custMutedAll}
          toggleCustMuteAll={toggleCustMuteAll}
          custMutedSvc={custMutedSvc}
          toggleCustChannel={toggleCustChannel}
          onClose={() => setShowSettings(false)}
        />
      )}
      {showNew && tab === 'internal' ? (
        <NewChatPanel onPicked={pick} onClose={() => setShowNew(false)} />
      ) : showNew && tab === 'customer' ? (
        <NewCustomerChatPanel onPicked={pick} onClose={() => setShowNew(false)} />
      ) : tab === 'observe' ? (
        <div className="chatlist" style={{ flex: 1, overflowY: 'auto' }}>
          <div className="small muted" style={{ padding: '6px 12px', lineHeight: 1.45 }}>
            Nhóm bạn <b>không ở trong</b> — chỉ theo dõi. Mặc định <b>tắt thông báo</b>; bấm 🔕→🔔 để nhận tin nhóm cần.
          </div>
          {!obsGroups.length && <div className="chatempty">Không có nhóm nào để giám sát (bạn đang ở trong tất cả các nhóm).</div>}
          {obsGroups.filter((g) => !q.trim() || (g.name || '').toLowerCase().includes(q.trim().toLowerCase())).map((g) => {
            const active = selLive && String(selLive.id) === String(g.id);
            return (
              <div key={g.id} className={'chatlist-row' + (active ? ' active' : '')} onClick={() => pick({ ...g, observer: true })}>
                <Ava conv={{ type: 'group' }} />
                <div className="chatlist-main">
                  <div className="chatlist-top">
                    <span className="chatlist-name">{g.name}</span>
                    <span className="chatlist-time">{fmtTime(g.lastMessage?.at)}</span>
                  </div>
                  <div className="chatlist-preview">{g.lastMessage?.text || 'Chưa có tin'}</div>
                </div>
                <button type="button" className="chathead-btn" style={{ alignSelf: 'center', fontSize: 18 }}
                  title={g.watching ? 'Đang nhận thông báo — bấm để TẮT' : 'Bấm để BẬT nhận thông báo nhóm này'}
                  onClick={(e) => { e.stopPropagation(); toggleWatch(g); }}>
                  {g.watching ? '🔔' : '🔕'}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="chatlist" style={{ flex: 1, overflowY: 'auto' }}>
          {!filtered.length && <div className="chatempty">{tab === 'customer' ? 'Chưa có khách hàng nào nhắn tin.' : 'Không có đoạn chat nào.'}</div>}
          {/* Tab Khách hàng: GOM THEO KHÁCH (1 dòng/khách, các kênh thành chip). Tab Nội bộ: phẳng. */}
          {tab === 'customer'
            ? groupByCustomer(filtered).map((g) => {
                const rep = g.rep;
                const multi = g.list.length > 1;
                const active = selLive && g.list.some((c) => String(c.id) === String(selLive.id));
                const draftText = g.list.map((c) => chat.drafts?.[c.id]).find(Boolean);
                return (
                  <div key={g.key} className={'chatlist-row' + (g.unread ? ' unread' : '') + (active ? ' active' : '')} onClick={() => rowClick(rep)} {...rowMenu(targetOfGroup(g))}>
                    <div className="chatlist-ava">
                      <Ava conv={rep} size={40} />
                      {g.unread > 0 && <span className="chatlist-dot" />}
                    </div>
                    <div className="chatlist-main">
                      <div className="chatlist-top">
                        <span className="chatlist-name">
                          {g.pinned ? '📌 ' : ''}{g.starred && <span className="chatlist-star" title="KH doanh thu cao (được đánh dấu sao)">⭐</span>}{g.name}
                          {!multi && !rep.assignedTo && rep.svcType === 'kd' && canSeeAllKh(me) && <span className="chattag-warn">chưa gán</span>}
                        </span>
                      </div>
                      {canSeeSaleKh(me) && g.custSaleName && String(g.custSaleUser || '') !== String(me.id || me._id) && <div className="chatlist-sale">Sale: {g.custSaleName}</div>}
                      <div className="chatlist-preview">
                        {multi && <span className="small muted">{SVC_SHORT[rep.svcType] || 'KD'}: </span>}
                        {draftText || g.lastMessage?.text || 'Bắt đầu trò chuyện…'}
                      </div>
                      <div className="cust-chips">
                        {/* Tag NGUỒN khách (SocSon vs PMBox/jtool) + chip kênh ĐANG CÓ hội thoại. */}
                        {(() => { const src = rep.channel === 'jtool' ? 'jtool' : 'socson'; return <span className={'cust-src cust-src-' + src} title={src === 'jtool' ? 'Khách từ PMBox (jtool)' : 'Khách từ SocSon'}>{src === 'jtool' ? 'PMBox' : 'SocSon'}</span>; })()}
                        {SVC_ORDER.filter((t) => g.chans[t]).map((t) => {
                          const n = g.chans[t].unread || 0;
                          return <span key={t} className={'cust-chip' + (n ? ' has' : '')}>{SVC_SHORT[t]}{n > 0 && <b>{n > 9 ? '9+' : n}</b>}</span>;
                        })}
                      </div>
                    </div>
                    <div className="chatlist-meta">
                      {draftText
                        ? <span className="chatlist-time" style={{ color: 'var(--danger)', fontWeight: 600 }}>Chưa gửi</span>
                        : <span className="chatlist-time">{fmtTime(g.lastMessage?.at)}</span>}
                      {g.unread > 0 && <span className="chatlist-badge">{g.unread}</span>}
                    </div>
                    <button type="button" className={'chatlist-pinbtn' + (g.pinned ? ' on' : '')}
                      title={g.pinned ? 'Bỏ ghim' : 'Ghim lên đầu (tối đa 3)'} onClick={(e) => togglePinConv(e, rep)}>📌</button>
                  </div>
                );
              })
            : filtered.map((c) => {
            const online = c.kind !== 'customer' && c.type === 'direct' && c.other?.id && onlineIds?.has(String(c.other.id));
            const active = selLive && String(selLive.id) === String(c.id);
            return (
              <div key={c.id} className={'chatlist-row' + (c.unread ? ' unread' : '') + (active ? ' active' : '')} onClick={() => rowClick(c)} {...rowMenu(targetOfConv(c))}>
                <div className="chatlist-ava">
                  <Ava conv={c} size={40} />
                  {online && <span className="chat-online-dot" title="Đang online" />}
                  {c.unread > 0 && <span className="chatlist-dot" />}
                </div>
                <div className="chatlist-main">
                  <div className="chatlist-top">
                    <span className="chatlist-name">
                      {c.pinned ? '📌 ' : ''}{c.muted ? '🔕 ' : ''}{c.name}
                    </span>
                  </div>
                  <div className="chatlist-preview">{chat.drafts?.[c.id] || c.lastMessage?.text || 'Bắt đầu trò chuyện…'}</div>
                </div>
                <div className="chatlist-meta">
                  {chat.drafts?.[c.id]
                    ? <span className="chatlist-time" style={{ color: 'var(--danger)', fontWeight: 600 }}>Chưa gửi</span>
                    : <span className="chatlist-time">{fmtTime(c.lastMessage?.at)}</span>}
                  {c.unread > 0 && <span className="chatlist-badge">{c.unread}</span>}
                </div>
                <button type="button" className={'chatlist-pinbtn' + (c.pinned ? ' on' : '')}
                  title={c.pinned ? 'Bỏ ghim hội thoại' : 'Ghim hội thoại lên đầu (tối đa 3)'}
                  onClick={(e) => togglePinConv(e, c)}>📌</button>
              </div>
            );
          })}
          {/* KẾT QUẢ THEO NỘI DUNG TIN NHẮN (kiểu FB) — tên ưu tiên phía trên, tin nhắn bên dưới. */}
          {q.trim().length >= 2 && (
            <div className="chatpage-msghits">
              <div className="chatpage-msghits-head">Tin nhắn</div>
              {msgHits === null ? (
                <div className="small muted" style={{ padding: '6px 12px' }}>Đang tìm…</div>
              ) : !msgHits.length ? (
                <div className="small muted" style={{ padding: '6px 12px' }}>Không có tin nhắn nào chứa “{q.trim()}”.</div>
              ) : (
                msgHits.map((h) => (
                  <div key={h.msgId} className="chatlist-row" onClick={() => openHit(h)} title="Mở đoạn chat và cuộn tới tin này">
                    <div className="chatlist-ava"><div className="chat-avatar msghit">🔎</div></div>
                    <div className="chatlist-main">
                      <div className="chatlist-top">
                        <span className="chatlist-name">{h.convName}</span>
                        <span className="chatlist-time">{fmtTime(h.at)}</span>
                      </div>
                      <div className="chatlist-preview">{h.sender ? `${h.sender}: ` : ''}{excerpt(h.text, q.trim())}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const threadCol = (
    <div className="chatpage-col chatpage-mid">
      {selLive ? (
        <div className="chatpage-thread">
          {selLive.kind === 'customer'
            ? <CustomerChatWindow key={'k' + selLive.id} conv={selLive} front onBack={mobile ? () => setSel(null) : undefined} onTitleClick={mobile ? undefined : () => setShowInfo((v) => !v)} onSwitchChannel={(ch) => setSel(ch)} />
            : <ChatWindow key={'n' + selLive.id} conv={selLive} front onBack={mobile ? () => setSel(null) : undefined} onTitleClick={mobile ? undefined : () => setShowInfo((v) => !v)} />}
        </div>
      ) : (
        <div className="chatempty" style={{ margin: 'auto' }}>Chọn một đoạn chat để bắt đầu</div>
      )}
    </div>
  );

  if (mobile) {
    return <div className="chatpage chatpage-mobile">{selLive ? threadCol : listCol}{convMenuEl}</div>;
  }
  return (
    <div className={'chatpage' + (showInfo && selLive ? '' : ' noinfo')}>
      {listCol}
      {threadCol}
      {showInfo && selLive && <InfoPanel key={(selLive.kind || 'n') + selLive.id} conv={selLive} />}
      {convMenuEl}
    </div>
  );
}

// Ô "Cuộc trò chuyện mới" trong tab Nội bộ: chat riêng (bấm tên) hoặc tạo nhóm (chọn nhiều + đặt tên).
// Panel "Nhắn khách hàng mới": tìm KH theo mã/tên -> tạo (hoặc mở lại) hội thoại KH rồi vào chat luôn.
function NewCustomerChatPanel({ onPicked, onClose }) {
  const { refreshCust } = useChat();
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [channels, setChannels] = useState([]);
  const [sel, setSel] = useState(null); // KH đang chọn kênh/mã
  useEffect(() => { getMyChannels().then(setChannels).catch(() => setChannels([])); }, []);
  useEffect(() => {
    const t = q.trim();
    if (!t) { setOpts([]); return; }
    const id = setTimeout(() => searchCustomers(t, true, true).then(setOpts).catch(() => {}), 250); // forChat: CSKH/DVKH tìm được mọi KH
    return () => clearTimeout(id);
  }, [q]);
  function pick(o) {
    if (busy) return;
    if (needsChatChoice(o, channels)) { setSel(o); setErr(''); }
    else startChat(o.code, channels[0], o.source);
  }
  async function startChat(code, svc, source) {
    if (busy) return;
    setBusy(true); setErr('');
    try {
      const conv = await createCustConversation(code, svc, source);
      refreshCust(); // danh sách tab Khách hàng tự có hội thoại mới
      onPicked({ ...conv, id: conv._id, kind: 'customer', name: conv.custName || conv.custCode || 'Khách hàng' });
      onClose();
    } catch (e) {
      setErr(e.response?.data?.message || 'Không tạo được hội thoại');
    } finally { setBusy(false); }
  }
  return (
    <div className="chatuserlist" style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
      <input className="chat-input" autoFocus style={{ width: '100%' }} value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="🔍 Tìm mã / tên khách hàng…" />
      {err && <div className="small" style={{ color: 'var(--danger)', marginTop: 8 }}>{err}</div>}
      {sel && <StartCustChat opt={sel} channels={channels} busy={busy} onStart={startChat} onCancel={() => setSel(null)} />}
      {!q.trim() && !sel && <div className="chatempty">Gõ mã hoặc tên KH để bắt đầu nhắn tin chủ động.</div>}
      {q.trim() && !opts.length && <div className="chatempty">Không tìm thấy khách hàng.</div>}
      {opts.slice(0, 30).map((o) => (
        <div key={o.id || o.code} className="chatuser-row" style={{ cursor: busy ? 'wait' : 'pointer' }} onClick={() => pick(o)}>
          <span className="chat-avatar sm">{(o.name || o.code || '?').trim().split(/\s+/).slice(-1)[0]?.[0]?.toUpperCase() || '?'}</span>
          <div className="chatuser-main">
            <div className="chatuser-name">{o.name || o.code}</div>
            <div className="chatuser-sub"><code>{o.code}</code>{o.sale ? ` · sale: ${o.sale}` : ''}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function NewChatPanel({ onPicked, onClose }) {
  const { me, refresh } = useChat();
  const [users, setUsers] = useState([]);
  const [uq, setUq] = useState('');
  const [mode, setMode] = useState('direct'); // 'direct' | 'group'
  const [picked, setPicked] = useState(() => new Set());
  const [gname, setGname] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { chatUsers().then(setUsers).catch(() => {}); }, []);
  const myId = String(me.id || me._id);
  const options = users
    .filter((u) => String(u.id) !== myId)
    .filter((u) => !uq.trim() || u.name.toLowerCase().includes(uq.trim().toLowerCase()));

  const startDirectChat = async (u) => {
    if (busy) return;
    setBusy(true);
    try {
      const conv = await openDirect(u.id);
      refresh();
      onPicked(conv);
    } catch (e) { alert(e?.response?.data?.message || 'Không mở được'); } finally { setBusy(false); }
  };
  const doCreateGroup = async () => {
    if (busy || !gname.trim() || !picked.size) return;
    setBusy(true);
    try {
      const conv = await createGroup({ name: gname.trim(), memberIds: [...picked] });
      refresh();
      onPicked(conv);
    } catch (e) { alert(e?.response?.data?.message || 'Không tạo được nhóm'); } finally { setBusy(false); }
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: 6, padding: '8px 10px' }}>
        <button className={'btn sm' + (mode === 'direct' ? '' : ' ghost')} onClick={() => setMode('direct')}>Chat riêng</button>
        {/* Đối tác (DT) không được tạo nhóm — chỉ chat riêng với người được gán. */}
        {me?.role !== 'DT' && (
          <button className={'btn sm' + (mode === 'group' ? '' : ' ghost')} onClick={() => setMode('group')}>Tạo nhóm</button>
        )}
        <button className="chat-members-x" style={{ marginLeft: 'auto' }} onClick={onClose} title="Đóng">×</button>
      </div>
      {mode === 'group' && (
        <div style={{ padding: '0 10px 6px', display: 'flex', gap: 6 }}>
          <input className="chat-input" style={{ flex: 1 }} value={gname} onChange={(e) => setGname(e.target.value)} placeholder="Tên nhóm…" />
          <button className="btn sm" disabled={busy || !gname.trim() || !picked.size} onClick={doCreateGroup}>Tạo ({picked.size})</button>
        </div>
      )}
      <div style={{ padding: '0 10px 6px' }}>
        <input className="chat-input" style={{ width: '100%' }} value={uq} onChange={(e) => setUq(e.target.value)} placeholder="🔍 Tìm nhân viên…" />
      </div>
      <div className="chatlist" style={{ flex: 1, overflowY: 'auto' }}>
        {options.map((u) => (
          <div key={u.id} className="chatlist-row" onClick={() => (mode === 'direct' ? startDirectChat(u) : setPicked((prev) => { const s = new Set(prev); s.has(u.id) ? s.delete(u.id) : s.add(u.id); return s; }))}>
            <div className="chatlist-ava">
              <div className="chat-avatar">{u.avatar ? <img className="chat-avatar-img" src={u.avatar} alt="" /> : initials(u.name)}</div>
            </div>
            <div className="chatlist-main">
              <div className="chatlist-top"><span className="chatlist-name">{u.name}</span></div>
              <div className="chatlist-preview">{(u.departments || []).join(', ') || u.role}</div>
            </div>
            {mode === 'group' && <input type="checkbox" readOnly checked={picked.has(u.id)} />}
          </div>
        ))}
      </div>
    </div>
  );
}

// Mục gập/mở trong bảng thông tin. ĐỊNH NGHĨA Ở MODULE-LEVEL (không lồng trong InfoPanel) —
// nếu lồng, mỗi lần gõ ô tìm kiếm sẽ tạo component MỚI -> React tháo/gắn lại cả cây -> ô input
// mất focus + HUỶ bộ gõ tiếng Việt (đang gõ dở bị vỡ). Đưa ra ngoài để cây con ổn định.
function Section({ id, title, openSec = {}, setOpenSec = () => {}, children }) {
  return (
    <div className="chatpage-sec">
      <button type="button" className="chatpage-sechead" onClick={() => setOpenSec((s) => ({ ...s, [id]: !s[id] }))}>
        <span>{title}</span><span>{openSec[id] ? '▾' : '▸'}</span>
      </button>
      {openSec[id] && <div className="chatpage-secbody">{children}</div>}
    </div>
  );
}

// Mục CÔNG VIỆC trong bảng thông tin — bọc ChatTasksPanel (dùng chung desktop/mobile) trong 1 Section
// gập/mở. Module-level để ổn định bộ gõ tiếng Việt.
function TasksSection({ tasks, assigneeOptions, me, onCreate, onAccept, onComplete, onDelete, openSec, setOpenSec }) {
  const openCount = openTaskCount(tasks);
  return (
    <Section id="tasks" title={`Công việc${openCount ? ` (${openCount})` : ''}`} openSec={openSec} setOpenSec={setOpenSec}>
      <ChatTasksPanel tasks={tasks} assigneeOptions={assigneeOptions} me={me}
        onCreate={onCreate} onAccept={onAccept} onComplete={onComplete} onDelete={onDelete} />
    </Section>
  );
}

// Bảng thông tin bên phải — đổi theo loại hội thoại (KH / nhóm / 1-1).
function InfoPanel({ conv }) {
  const { me, onlineIds, patchConv, patchCustConv, removeConvsLocal } = useChat();
  // Giải tán (xoá) nhóm — chỉ NGƯỜI TẠO (👑 Trưởng nhóm) hoặc GĐ/CV. Backend chốt lại quyền.
  const canDisband = String(conv.createdBy || '') === String(me.id || me._id) || me.role === 'GD' || me.role === 'CV';
  async function disbandGroup() {
    if (!window.confirm(`Giải tán nhóm "${conv.name}"?\n\nToàn bộ tin nhắn của nhóm sẽ bị xoá, KHÔNG khôi phục được. Nhóm biến mất khỏi danh sách của tất cả thành viên.`)) return;
    try {
      await deleteGroupChat(conv.id);
      removeConvsLocal([conv.id]);
      window.dispatchEvent(new CustomEvent('ft:conv-removed', { detail: { id: conv.id } }));
    } catch (e) {
      alert(e?.response?.data?.message || 'Giải tán nhóm thất bại');
    }
  }
  const isCust = conv.kind === 'customer';
  const isGroup = !isCust && conv.type === 'group';
  const [users, setUsers] = useState([]);
  const [openSec, setOpenSec] = useState({ info: true, media: true, search: false });
  const [sq, setSq] = useState('');
  const [sres, setSres] = useState(null);
  const [busy, setBusy] = useState(false);
  const sqTimer = useRef(null);
  // Trưởng nhóm (người tạo) được XOÁ thành viên NGAY tại bảng thông tin (parity với nút 👥 ở widget).
  // Backend chốt quyền: chỉ createdBy mới xoá được, không xoá được chính trưởng nhóm.
  const isCreator = isGroup && String(conv.createdBy || '') === String(me.id || me._id);
  const canRemove = isCreator || me.role === 'GD'; // trưởng nhóm HOẶC Giám đốc được xoá thành viên
  async function removeMember(uid, name) {
    if (busy) return;
    if (!window.confirm(`Xoá ${name} khỏi nhóm?`)) return;
    setBusy(true);
    try {
      const updated = await removeGroupMember(conv.id, uid);
      patchConv(conv.id, { members: updated.members });
    } catch (e) {
      alert(e?.response?.data?.message || 'Xoá thành viên thất bại');
    } finally { setBusy(false); }
  }
  // Đổi tên nhóm — chỉ trưởng nhóm (người tạo) hoặc Giám đốc (dùng chung quyền canRemove). Backend chốt lại.
  async function renameGroupChat() {
    if (busy) return;
    const next = window.prompt('Đổi tên nhóm:', conv.name || '');
    if (next == null) return;
    const name = next.trim();
    if (!name || name === conv.name) return;
    setBusy(true);
    try {
      const updated = await renameGroup(conv.id, name);
      patchConv(conv.id, { name: updated.name });
    } catch (e) {
      alert(e?.response?.data?.message || 'Đổi tên nhóm thất bại');
    } finally { setBusy(false); }
  }

  useEffect(() => {
    // Danh sách nhân viên: chat nội bộ để hiện thông tin 1-1; chat KH để chọn người GIAO việc.
    chatUsers().then(setUsers).catch(() => {});
  }, [conv.id]);

  // ===== CÔNG VIỆC trong chat =====
  // Người được giao: chat nội bộ = thành viên nhóm; chat KH = nhân viên (chỉ NV thấy công việc KH).
  const taskAssignees = isCust
    ? (users || []).map((u) => ({ id: u.id, name: u.name }))
    : (conv.members || []).map((m) => ({ id: m.id, name: m.name }));
  const applyTasks = (patch) => (isCust ? patchCustConv(conv.id, patch) : patchConv(conv.id, patch));
  const taskCreate = async (payload) => {
    const r = isCust ? await createCustTask(conv.id, payload) : await createChatTask(conv.id, payload);
    applyTasks({ tasks: [...(conv.tasks || []), r.task] });
  };
  const taskAccept = async (taskId) => {
    try {
      const r = isCust ? await acceptCustTask(conv.id, taskId) : await acceptChatTask(conv.id, taskId);
      applyTasks({ tasks: (conv.tasks || []).map((t) => (t.id === taskId ? r.task : t)) });
    } catch (e) { alert(e?.response?.data?.message || 'Không tiếp nhận được công việc'); }
  };
  const taskComplete = async (taskId, done) => {
    try {
      const r = isCust ? await completeCustTask(conv.id, taskId, done) : await completeChatTask(conv.id, taskId, done);
      applyTasks({ tasks: (conv.tasks || []).map((t) => (t.id === taskId ? r.task : t)) });
    } catch (e) { alert(e?.response?.data?.message || 'Không cập nhật được công việc'); }
  };
  const taskDelete = async (taskId) => {
    if (!window.confirm('Xoá công việc này?')) return;
    try {
      if (isCust) await deleteCustTask(conv.id, taskId); else await deleteChatTask(conv.id, taskId);
      applyTasks({ tasks: (conv.tasks || []).filter((t) => t.id !== taskId) });
    } catch (e) { alert(e?.response?.data?.message || 'Xoá công việc thất bại'); }
  };

  // Tìm trong đoạn chat: gõ xong 400ms mới gọi.
  useEffect(() => {
    clearTimeout(sqTimer.current);
    if (!sq.trim()) { setSres(null); return; }
    sqTimer.current = setTimeout(() => {
      (isCust ? custSearchMessages(conv.id, sq.trim()) : searchChatMessages(conv.id, sq.trim()))
        .then(setSres).catch(() => setSres([]));
    }, 400);
    return () => clearTimeout(sqTimer.current);
  }, [sq, conv.id, isCust]);

  const jumpTo = (id) => {
    const el = document.getElementById((isCust ? 'kmsg-' : 'cmsg-') + id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('chatmsg-flash');
      setTimeout(() => el.classList.remove('chatmsg-flash'), 1400);
      return;
    }
    // Tin cũ chưa tải -> nhờ khung chat tải thêm tin cũ tới khi thấy rồi cuộn tới (khỏi tự kéo lên).
    window.dispatchEvent(new CustomEvent('ft:jump-msg', { detail: { convId: conv.id, id } }));
  };

  const toggleMute = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (isCust) {
        const r = await custMute(conv.id, !conv.muted);
        patchCustConv(conv.id, { muted: r.muted });
      } else {
        const r = await muteConversation(conv.id, !conv.muted);
        patchConv(conv.id, { muted: r.muted });
      }
    } catch { /* giữ nguyên */ } finally { setBusy(false); }
  };

  const other = !isCust && conv.type === 'direct' ? users.find((u) => String(u.id) === String(conv.other?.id)) : null;

  return (
    <div className="chatpage-col chatpage-info">
      <div style={{ textAlign: 'center', padding: '16px 10px 8px' }}>
        <div style={{ display: 'inline-block' }}><Ava conv={conv} size={56} /></div>
        <div style={{ fontWeight: 700, marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <span>{conv.name}</span>
          {isGroup && canRemove && (
            <button onClick={renameGroupChat} disabled={busy} title="Đổi tên nhóm"
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--muted)', fontSize: 14, padding: 2, lineHeight: 1 }}>✎</button>
          )}
        </div>
        <div className="small muted">
          {isCust ? `Mã KH ${conv.custCode || '—'}` : isGroup ? `${(conv.members || []).length} thành viên` : (other?.departments || []).join(', ') || other?.role || ''}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 10 }}>
          <button className="btn sm" disabled={busy} onClick={toggleMute}>{conv.muted ? '🔕 Đang tắt âm' : '🔔 Tắt thông báo'}</button>
          <button className="btn sm ghost" onClick={() => setOpenSec((s) => ({ ...s, search: !s.search }))}>🔍 Tìm kiếm</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {openSec.search && (
          <Section id="search" title="Tìm trong đoạn chat" openSec={openSec} setOpenSec={setOpenSec}>
            <input className="chat-input" style={{ width: '100%' }} autoFocus value={sq} onChange={(e) => setSq(e.target.value)} placeholder="Nhập chữ cần tìm…" />
            {sres && (
              <div style={{ marginTop: 6 }}>
                {!sres.length && <div className="small muted">Không thấy tin nào.</div>}
                {sres.map((m) => (
                  <div key={m._id} className="chatpage-hit" onClick={() => jumpTo(m._id)} title="Bấm để nhảy tới tin">
                    <div className="small"><b>{m.sender}</b> · <span className="muted">{fmtTime(m.at)}</span></div>
                    <div className="small chatpage-hittext">{m.text}</div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {isCust && (
          <Section id="info" title="Thông tin khách hàng" openSec={openSec} setOpenSec={setOpenSec}>
            <div className="small">Mã KH: <b>{conv.custCode || '—'}</b></div>
            <div className="small">SĐT: {conv.custPhone || '—'}</div>
            <div className="small">Phụ trách: {conv.assignedName || 'chưa gán'}</div>
            <div className="small">Nguồn: {conv.channel === 'jtool' ? 'PMBox' : 'SocSon'}</div>
          </Section>
        )}

        {isGroup && (
          <Section id="info" title={`Thành viên (${(conv.members || []).length})`} openSec={openSec} setOpenSec={setOpenSec}>
            {(conv.members || []).map((mm) => {
              const on = onlineIds?.has(String(mm.id));
              return (
                <div key={mm.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <span className="chat-avatar xs" style={{ position: 'relative' }}>
                    {mm.avatar ? <img className="chat-avatar-img" src={mm.avatar} alt="" /> : initials(mm.name)}
                  </span>
                  <span className="small" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {mm.name}{String(mm.id) === String(me.id || me._id) ? ' (bạn)' : ''}
                    {String(conv.createdBy || '') === String(mm.id) && <span className="chat-leader-badge" title="Trưởng nhóm (người tạo nhóm)">👑 Trưởng nhóm</span>}
                  </span>
                  <span className="small" style={{ color: on ? 'var(--ok,#16a34a)' : 'var(--muted)' }}>{on ? '●' : '○'}</span>
                  {canRemove && String(mm.id) !== String(conv.createdBy) && (
                    <button className="chat-members-remove" title={`Xoá ${mm.name} khỏi nhóm`} disabled={busy}
                      onClick={() => removeMember(mm.id, mm.name)}>✕</button>
                  )}
                </div>
              );
            })}
            <div className="small muted" style={{ marginTop: 4 }}>
              {canRemove ? 'Bấm ✕ để xoá thành viên · Thêm thành viên: nút 👥 trên khung chat.' : 'Thêm/xoá thành viên: nút 👥 trên khung chat.'}
            </div>
            {canDisband && (
              <button className="chat-delgroup-btn" onClick={disbandGroup} title="Giải tán nhóm (không khôi phục được)" style={{ marginTop: 10 }}>
                🗑 Giải tán nhóm
              </button>
            )}
          </Section>
        )}

        {!isCust && conv.type === 'direct' && other && (
          <Section id="info" title="Thông tin nhân viên" openSec={openSec} setOpenSec={setOpenSec}>
            <div className="small">Chức vụ: {other.isDeputy ? 'Phó phòng' : other.role === 'GD' ? 'Giám đốc' : other.role === 'CV' ? 'Cố vấn' : other.role === 'TP' ? 'Trưởng phòng' : 'Nhân viên'}</div>
            <div className="small">Phòng ban: {(other.departments || []).join(', ') || '—'}</div>
            <div className="small" style={{ color: onlineIds?.has(String(other.id)) ? 'var(--ok,#16a34a)' : 'var(--muted)' }}>
              {onlineIds?.has(String(other.id)) ? '● Đang online' : '○ Ngoại tuyến'}
            </div>
          </Section>
        )}

        <TasksSection
          tasks={conv.tasks}
          assigneeOptions={taskAssignees}
          me={me}
          onCreate={taskCreate}
          onAccept={taskAccept}
          onComplete={taskComplete}
          onDelete={taskDelete}
          openSec={openSec}
          setOpenSec={setOpenSec}
        />

        <Section id="media" title="Kho lưu trữ" openSec={openSec} setOpenSec={setOpenSec}>
          <MediaVault conv={conv} isCust={isCust} />
        </Section>
      </div>
    </div>
  );
}

// ---- Kho lưu trữ (Ảnh/Video · File · Link) — tách 3 tab, nhóm theo NGÀY kiểu Zalo ----

// Icon theo đuôi tệp (app dùng emoji, không nạp webfont icon).
function vaultFileIcon(name) {
  const ext = (String(name || '').split('.').pop() || '').toLowerCase();
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (ext === 'pdf') return '📕';
  if (['ppt', 'pptx'].includes(ext)) return '📙';
  if (['zip', 'rar', '7z'].includes(ext)) return '🗜️';
  if (['mp3', 'wav', 'm4a', 'aac'].includes(ext)) return '🎵';
  if (['stl', 'obj', '3mf', 'step', 'stp', 'gcode'].includes(ext)) return '🧊'; // tệp 3D (nghề in 3D)
  return '📎';
}

// Nhãn ngày: Hôm nay / Hôm qua / dd/mm/yyyy.
function vaultDayLabel(at) {
  const d = new Date(at);
  if (isNaN(d.getTime())) return '—';
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(new Date()) - startOf(d)) / 86400000);
  if (diff <= 0) return 'Hôm nay';
  if (diff === 1) return 'Hôm qua';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

// Gom danh sách (đã sắp mới->cũ) thành [{ label, items }] theo ngày, GIỮ thứ tự.
function vaultGroupByDay(items) {
  const out = [];
  let cur = null;
  for (const it of items) {
    const label = vaultDayLabel(it.at);
    if (!cur || cur.label !== label) { cur = { label, items: [] }; out.push(cur); }
    cur.items.push(it);
  }
  return out;
}

const VaultEmpty = ({ text }) => <div className="small muted" style={{ padding: '14px 2px' }}>{text}</div>;

function MediaVault({ conv, isCust }) {
  const [tab, setTab] = useState('media');
  const [media, setMedia] = useState(null); // ảnh+video+file (endpoint /media). null = đang tải
  const [links, setLinks] = useState(null); // tải LƯỜI khi mở tab Link lần đầu

  useEffect(() => {
    setMedia(null); setLinks(null); setTab('media');
    (isCust ? custListMedia(conv.id) : listChatMedia(conv.id)).then(setMedia).catch(() => setMedia([]));
  }, [conv.id, isCust]);

  useEffect(() => {
    if (tab === 'link' && links == null) {
      (isCust ? custListLinks(conv.id) : listChatLinks(conv.id)).then(setLinks).catch(() => setLinks([]));
    }
  }, [tab, links, conv.id, isCust]);

  const isVisual = (a) => { const t = (a.type || '').toLowerCase(); return t.startsWith('image/') || t.startsWith('video/'); };
  const visuals = (media || []).filter(isVisual);
  const files = (media || []).filter((a) => !isVisual(a));

  return (
    <div className="chatvault">
      <div className="chatvault-tabs">
        <button type="button" className={'chatvault-tab' + (tab === 'media' ? ' on' : '')} onClick={() => setTab('media')}>Ảnh/Video</button>
        <button type="button" className={'chatvault-tab' + (tab === 'file' ? ' on' : '')} onClick={() => setTab('file')}>File</button>
        <button type="button" className={'chatvault-tab' + (tab === 'link' ? ' on' : '')} onClick={() => setTab('link')}>Link</button>
      </div>

      {tab === 'media' && (
        media == null ? <VaultEmpty text="Đang tải…" />
          : !visuals.length ? <VaultEmpty text="Chưa có ảnh/video nào." />
            : vaultGroupByDay(visuals).map((g) => (
              <div key={g.label} className="chatvault-grp">
                <div className="chatvault-day">{g.label}</div>
                <div className="chatpage-mediagrid">
                  {g.items.map((a, i) => {
                    const url = mediaUrl(a.url);
                    const isVid = (a.type || '').toLowerCase().startsWith('video/');
                    return (
                      <a key={i} href={url} target="_blank" rel="noreferrer" className="chatvault-th" title={`${a.name || ''}${a.sender ? ' · ' + a.sender : ''}`}>
                        {isVid ? (<><video src={url} preload="metadata" muted playsInline /><span className="chatvault-play">▶</span></>)
                          : <img src={a.thumbUrl ? mediaUrl(a.thumbUrl) : url} alt={a.name || ''} loading="lazy" decoding="async" />}
                      </a>
                    );
                  })}
                </div>
              </div>
            ))
      )}

      {tab === 'file' && (
        media == null ? <VaultEmpty text="Đang tải…" />
          : !files.length ? <VaultEmpty text="Chưa có tệp nào." />
            : vaultGroupByDay(files).map((g) => (
              <div key={g.label} className="chatvault-grp">
                <div className="chatvault-day">{g.label}</div>
                {g.items.map((a, i) => {
                  const url = mediaUrl(a.url);
                  return (
                    <a key={i} className="chatvault-row" href={url} download={a.name} target="_blank" rel="noreferrer" onClick={onAttachmentClick(url, a.name)}>
                      <span className="chatvault-fic">{vaultFileIcon(a.name)}</span>
                      <span className="chatvault-meta">
                        <span className="chatvault-t">{a.name || 'tệp'}</span>
                        <span className="chatvault-m">{fmtSize(a.size)}{a.sender ? ' · ' + a.sender : ''}</span>
                      </span>
                      <span className="chatvault-dl">⬇</span>
                    </a>
                  );
                })}
              </div>
            ))
      )}

      {tab === 'link' && (
        links == null ? <VaultEmpty text="Đang tải…" />
          : !links.length ? <VaultEmpty text="Chưa có liên kết nào." />
            : vaultGroupByDay(links).map((g) => (
              <div key={g.label} className="chatvault-grp">
                <div className="chatvault-day">{g.label}</div>
                {g.items.map((l, i) => (
                  <a key={i} className="chatvault-row" href={l.url} target="_blank" rel="noreferrer" title={l.url}>
                    <span className="chatvault-fic chatvault-linkic">🔗</span>
                    <span className="chatvault-meta">
                      <span className="chatvault-t">{l.domain || l.url}</span>
                      <span className="chatvault-m">{l.url}</span>
                    </span>
                  </a>
                ))}
              </div>
            ))
      )}
    </div>
  );
}
